import { supabaseFetch } from './core';
import { devLog } from '../log';

// ============================================
// PROFILES
// ============================================

export const getProfile = async (userId, token = null) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getProfile called for userId:', userId);

  try {
    const data = await supabaseFetch(`/profiles?id=eq.${userId}&select=*`, {}, 0, token);
    /*#__PURE__*/ devLog('💾 [DATABASE] getProfile response - data:', data);

    if (!data || data.length === 0) {
      console.warn('⚠️ [DATABASE] Profile not found, returning null');
      return null;
    }

    /*#__PURE__*/ devLog('✅ [DATABASE] getProfile successful, returning:', data[0]);
    return data[0];
  } catch (error) {
    console.error('❌ [DATABASE] getProfile error:', error);
    console.error('❌ [DATABASE] Error message:', error.message);

    // Se è un 404 o "no rows", ritorna null
    if (error.message.includes('404') || error.message.includes('no rows')) {
      console.warn('⚠️ [DATABASE] Profile not found (404), returning null');
      return null;
    }

    throw error;
  }
};

// Crea un profilo se non esiste
export const createProfile = async (userId, name, role, email = null, token = null, consent = null) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] createProfile called');
  /*#__PURE__*/ devLog('💾 [DATABASE] userId:', userId);
  /*#__PURE__*/ devLog('💾 [DATABASE] name:', name);
  /*#__PURE__*/ devLog('💾 [DATABASE] role:', role);
  /*#__PURE__*/ devLog('💾 [DATABASE] email:', email);

  try {
    const profileData = {
      id: userId,
      name: name || 'Utente',
      role: role || 'athlete'
    };

    // Aggiungi email se fornita
    if (email) {
      profileData.email = email;
    }

    // Consenso/accettazione documenti (dai metadata di registrazione)
    if (consent) {
      if (consent.privacy_read_at) profileData.privacy_read_at = consent.privacy_read_at;
      if (consent.privacy_version) profileData.privacy_version = consent.privacy_version;
      if (consent.terms_accepted_at) profileData.terms_accepted_at = consent.terms_accepted_at;
      if (consent.terms_version) profileData.terms_version = consent.terms_version;
    }

    const data = await supabaseFetch('/profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    }, 0, token);

    /*#__PURE__*/ devLog('💾 [DATABASE] createProfile response - data:', data);
    /*#__PURE__*/ devLog('✅ [DATABASE] createProfile successful');

    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] createProfile error:', error);
    console.error('❌ [DATABASE] Error message:', error.message);
    throw error;
  }
};

// Cancellazione self-service dell'account (hard-delete immediato via RPC sicura).
// Elimina profilo + dati collegati (cascade) e l'utente auth. Irreversibile.
export const deleteMyAccount = async () => {
  await supabaseFetch('/rpc/delete_my_account', {
    method: 'POST',
    body: JSON.stringify({}),
  });
  return true;
};

export const updateProfile = async (userId, updates) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] updateProfile called');
  /*#__PURE__*/ devLog('💾 [DATABASE] userId:', userId);
  /*#__PURE__*/ devLog('💾 [DATABASE] updates:', updates);

  try {
    const data = await supabaseFetch(`/profiles?id=eq.${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] updateProfile successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateProfile error:', error);
    throw error;
  }
};

// Admin function to update any user's profile
export const updateUserProfile = async (userId, updates) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] updateUserProfile (admin) called');
  /*#__PURE__*/ devLog('💾 [DATABASE] userId:', userId);
  /*#__PURE__*/ devLog('💾 [DATABASE] updates:', updates);

  try {
    const data = await supabaseFetch(`/profiles?id=eq.${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] updateUserProfile successful');
    return Array.isArray(data) ? data[0] : data;
  } catch (error) {
    console.error('❌ [DATABASE] updateUserProfile error:', error);
    throw error;
  }
};

// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * Get all profiles, optionally filtered by role
 * @param {string|null} role - Optional role filter ('coach', 'athlete', 'admin')
 * @returns {Array} - Array of profile objects
 */
export const getAllProfiles = async (role = null) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAllProfiles called with role:', role);

    let endpoint = '/profiles?select=id,name,email,role,created_at&order=created_at.desc';
    if (role) {
      endpoint = `/profiles?role=eq.${role}&select=id,name,email,role,created_at&order=created_at.desc`;
    }

    const profiles = await supabaseFetch(endpoint);
    /*#__PURE__*/ devLog('✅ [DATABASE] getAllProfiles successful, found', profiles?.length || 0, 'profiles');
    return profiles || [];
  } catch (error) {
    console.error('❌ [DATABASE] getAllProfiles error:', error);
    return [];
  }
};

/**
 * Get all athletes
 * @returns {Array} - Array of athlete profiles
 */
export const getAllAthletes = async () => {
  return getAllProfiles('athlete');
};

/**
 * Lista unica utenti per l'admin (via RPC admin-only): include coach_mode e i
 * contatori (atleti seguiti, programmi creati come coach). Ritorna [] se non admin.
 */
export const getAdminUsers = async () => {
  try {
    const rows = await supabaseFetch('/rpc/admin_list_users', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    console.error('❌ [DATABASE] getAdminUsers error:', error);
    return [];
  }
};

/**
 * Get all coaches
 * @returns {Array} - Array of coach profiles
 */
export const getAllCoaches = async () => {
  return getAllProfiles('coach');
};

/**
 * Get admin dashboard statistics
 * @returns {Object} - { totalAthletes, totalCoaches, totalPrograms, totalWorkouts }
 */
export const getAdminDashboardStats = async () => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAdminDashboardStats called');

    // Fetch all data in parallel
    const [athletes, coaches, programs, workoutLogs] = await Promise.all([
      supabaseFetch('/profiles?role=eq.athlete&select=id'),
      supabaseFetch('/profiles?role=eq.coach&select=id'),
      supabaseFetch('/programs?select=id'),
      supabaseFetch('/workout_logs?select=id')
    ]);

    const stats = {
      totalAthletes: athletes?.length || 0,
      totalCoaches: coaches?.length || 0,
      totalPrograms: programs?.length || 0,
      totalWorkouts: workoutLogs?.length || 0
    };

    /*#__PURE__*/ devLog('✅ [DATABASE] getAdminDashboardStats successful:', stats);
    return stats;
  } catch (error) {
    console.error('❌ [DATABASE] getAdminDashboardStats error:', error);
    return {
      totalAthletes: 0,
      totalCoaches: 0,
      totalPrograms: 0,
      totalWorkouts: 0
    };
  }
};
